import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

export default function ResetSenha() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Link inválido — solicite um novo em Esqueci minha senha");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      toast.error("As senhas não coincidem");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw.next });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 3000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao redefinir");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="reset-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-[#0A1128] mb-6">
          <ArrowLeft size={14} /> Voltar ao login
        </Link>
        {!token ? (
          <div className="text-center" data-testid="reset-invalid">
            <AlertCircle size={40} className="mx-auto text-red-500 mb-3" />
            <h2 className="font-display text-xl text-[#0A1128] mb-2">Link inválido</h2>
            <p className="text-sm text-gray-600">Solicite um novo link em <Link to="/esqueci-senha" className="text-[#D4AF37] hover:underline">Esqueci minha senha</Link>.</p>
          </div>
        ) : !done ? (
          <>
            <h1 className="font-display text-3xl text-[#0A1128] font-light mb-2">Redefinir senha</h1>
            <p className="text-sm text-gray-500 mb-6">Escolha uma nova senha para sua conta.</p>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1"><Lock size={12} /> Nova senha</label>
                <input required type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="w-full mt-1 border border-gray-200 rounded-lg px-4 py-3 text-sm font-mono focus:border-[#D4AF37] outline-none" data-testid="reset-new" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Confirmar nova senha</label>
                <input required type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="w-full mt-1 border border-gray-200 rounded-lg px-4 py-3 text-sm font-mono focus:border-[#D4AF37] outline-none" data-testid="reset-confirm" />
              </div>
              <p className="text-[10px] text-gray-500">Mínimo 8 caracteres com maiúscula, minúscula, número e símbolo.</p>
              <button type="submit" disabled={loading} className="w-full bg-[#0A1128] text-white py-3 rounded-lg text-sm font-bold uppercase tracking-wider hover:bg-[#14213D] disabled:opacity-50" data-testid="reset-submit">
                {loading ? "Redefinindo…" : "Redefinir senha"}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />
            <h2 className="font-display text-xl text-[#0A1128] mb-2">Senha redefinida!</h2>
            <p className="text-sm text-gray-600">Você será redirecionado para o login em alguns segundos.</p>
          </div>
        )}
      </div>
    </div>
  );
}
